import { TitleCasePipe } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Hospital } from '@shared/models/hospital.model';
import { ErrorService } from '@shared/services/error.service';
import { InstanceIdService } from '@shared/services/instance-id.service';
import { SubscriptionManager } from '@shared/services/subscription-manager.service';
import moment from 'moment';
import { posthog } from 'posthog-js';
import { Subject } from 'rxjs';
import { anesthNeedsGardeComponent } from '../../../hrsuite/anesth-needs-garde/anesth-needs-garde.component';
import { SimpleDayNeedsComponent } from '../../../hrsuite/simple-day-needs/simple-day-needs.component';
import { Formation } from '../../models/formation.model';
import { Paramedical } from '../../models/paramedical.model';
import { Role } from '../../models/role.model';
import { User } from '../../models/user.model';
import { UserService } from '../../services/user.service';
import { EffectiveNeedsDetailsPopupComponent } from './effective-needs-details-popup/effective-needs-details-popup.component';
import {
  Data,
  DayDetails,
  EffectifByReasonMap,
  Effective,
  LastUpdateDate,
  Need,
  NightData,
  NightDetails,
  NightEffective,
  NightNeed,
  TotalCombined,
  TotalSeparated,
} from './interfaces';

moment.locale('fr');

const PERIOD = {
  DAY: 'DAY',
  NIGHT: 'NIGHT',
};

@Component({
  selector: 'app-effective-needs',
  templateUrl: './effective-needs.component.html',
  styleUrls: ['./effective-needs.component.scss'],
  standalone: false,
})
export class EffectiveNeedsComponent implements OnInit, OnChanges {
  private instanceId: string;

  constructor(
    private readonly userService: UserService,
    private readonly dialog: MatDialog,
    private readonly errorService: ErrorService,
    private readonly titleCasePipe: TitleCasePipe,
    private destroyRef: DestroyRef,
    private subscriptionManager: SubscriptionManager,
    private instanceIdService: InstanceIdService,
    private elementRef: ElementRef
  ) {
    this.instanceId = this.instanceIdService.generateId();
    this.subscriptionManager.registerComponent(this.instanceId, this.destroyRef);
    this.currentUser = this.userService.getCurrentUser();
  }

  @Input() public dayIndex: number | undefined;

  @Input() public reasonMap: any | undefined;

  @Input() public initialReasonMap: any | undefined;

  @Input() public date: Date | undefined;

  @Input() public coveragePercentage?: { percentage: number; percentageLabel: string };

  @Input() public effective: Effective | NightEffective | undefined;

  @Input() public lastUpdateDate: LastUpdateDate | undefined;

  @Input() public needs: Need[] | undefined;

  @Input() public nightNeeds: NightNeed[] | undefined;

  @Input() public formations: Formation[] | undefined;

  @Input() public paramedicRules: Paramedical[] | undefined;

  @Input() public separateMorningAfternoon = true;

  @Input() public onlyMorning = false;

  @Input() public onlyAfternoon = false;

  @Input() public period: string = PERIOD.DAY;

  @Input() public isFetching = true;

  @Input() public hospitals: string[] | undefined;

  @Input() public isCycleConfig = false;

  @Input() public populations: any;

  @Input() public currentPopulation: string | undefined;

  @Input() public openDetailsPopupSubject: Subject<void> | undefined;

  @Input() public showDifference = false;

  @Input() public showStatusIcon = true;

  @Input() public showInformationsButton = false;

  @Input() public layout = 'row'; // 'row' | 'column'

  @Input() public showStatusIconOnly = false;

  @Input() public clickOnTextToOpenDetails = false;

  @Input() public isCommissionRegulationLayout = false;

  @Input() public isSmartPlanningDetailsLayout = false;

  @Input() public planningDayStatus: string | undefined;

  @Input() public isSmartPlanningSideColumnLayout = false;

  @Input() public isSurgeonOpeningsRoomsLayout = false;

  @Input() public isTVO = false;

  @Input() public dayOfTheWeek: string | undefined;

  @Input() public isMensuel: boolean = false;

  @Input() public isNotReadyYet: boolean = false;

  @Input() public formationsGroups: any[] = [];

  @Input() public isEffectiveNeedsByReasonMaps = false;

  @Input() public dayNeeds: number | undefined;

  @Input() public dayEffectifs: number | undefined;

  @Input() public status: string | undefined;

  @Input() public effectifByReasonMap: EffectifByReasonMap[] | undefined;

  @Input() public needsTables: any[] = [];

  @Input() public minimalistTitle = false;

  @Input() public campaignId: string | undefined = undefined;

  @Input() public scenarioId: string | undefined = undefined;

  @Input() public showCoveragePercentage: boolean = false;

  @Input() public isWeekly: boolean = false;

  @Input() public days: string[] = [];

  @Output() public onObjectifStatusChange: EventEmitter<string> = new EventEmitter<string>();

  // Day data
  public details: DayDetails[] | undefined;

  public total: TotalCombined | TotalSeparated | undefined;

  public totalNeedsEffectif: TotalSeparated | TotalCombined | undefined;

  public dayObjectifStatus: string | undefined;

  public nightObjectifStatus: string | undefined;

  // Night data
  public nightDetails: NightDetails[] | undefined;

  public nightTotal: TotalCombined | undefined;

  private readonly currentUser: User;

  private concernedHospitals: string[] | undefined;

  public isLoading = true;

  public populationsTotal: any[] = [];

  private notInitialized = true;

  public hoverEffectiveNeeds = false;

  public coverageBySkillsText: string = `
  <strong>IDEA — Indice d'Adéquation Équipe-Activité</strong>
    <br/>
    <br/>
    <span>Il s'agit d'un taux de couverture en expérimentation qui indique l'adéquation <br/>
    entre le personnel présent et ses compétences avec le programme de la journée.<br/>
     Plus il est élevé, plus le personnel présent est capable de couvrir de facon <br/>
      autonome (sans doublure) les besoins en salles de la journée</span>
  `;

  public isPastDate: boolean = false;

  public roleNeedsTotal: {
    afternoon: number;
    morning: number;
    needsByRole: { role: Role; morning: number; afternoon: number }[];
    hospital: any;
  } = {
    morning: 0,
    afternoon: 0,
    needsByRole: [] as { role: Role; morning: number; afternoon: number }[],
    hospital: {},
  };

  static calculateDayObjectifStatus(details: DayDetails[]): string {
    let effectiveIsLessThanNeedsInTotal;

    for (const item of details) {
      let effectiveIsLessThanNeedsForThisService;

      for (const item2 of item.data) {
        if (
          (item2.morning?.effective ?? 0) < (item2.morning?.needs ?? 0) ||
          (item2.afternoon?.effective ?? 0) < (item2.afternoon?.needs ?? 0)
        ) {
          item2.objectifStatus = 'ERROR';
          effectiveIsLessThanNeedsForThisService = true;
        } else {
          item2.objectifStatus = 'OK';
        }
      }

      if (effectiveIsLessThanNeedsForThisService) {
        item.objectifStatus = 'ERROR';
        effectiveIsLessThanNeedsInTotal = true;
      } else {
        item.objectifStatus = 'OK';
      }
    }

    if (effectiveIsLessThanNeedsInTotal) {
      return 'ERROR';
    } else {
      return 'OK';
    }
  }

  isOnRightSide(): boolean {
    const el = this.elementRef.nativeElement.getBoundingClientRect();
    return el.left > window.innerWidth / 2;
  }

  static calculateNightObjectifStatus(details: NightDetails[]): string {
    let effectiveIsLessThanNeedsInTotal = false;

    for (const item of details) {
      let effectiveIsLessThanNeedsForThisService = false;

      for (const item2 of item.data) {
        if ((item2.effective ?? 0) < (item2.needs ?? 0)) {
          item2.objectifStatus = 'ERROR';
          effectiveIsLessThanNeedsForThisService = true;
        } else {
          item2.objectifStatus = 'OK';
        }
      }

      if (effectiveIsLessThanNeedsForThisService) {
        item.objectifStatus = 'ERROR';
        effectiveIsLessThanNeedsInTotal = true;
      } else {
        item.objectifStatus = 'OK';
      }
    }

    if (effectiveIsLessThanNeedsInTotal) {
      return 'ERROR';
    } else {
      return 'OK';
    }
  }

  static constructCoreStructureOfDetailsDay(
    details: DayDetails[],
    formations: Formation[],
    paramedicRules: Paramedical[]
  ): void {
    const treatedFormations: any = {};

    for (const formation of formations) {
      if (!treatedFormations[String(formation._id)]) {
        // We didn't treat this formation yet

        // Construct groupedFormations
        const groupedFormations: Formation[] = [formation, ...(formation.groupedWith ?? [])];

        // Mark as treated
        for (const f of groupedFormations) {
          treatedFormations[String(f._id)] = true;
        }

        // Construct title
        const title = groupedFormations.reduce((previous: any, current: any) => {
          if (previous.length > 0) {
            return `${previous} / ${current.name}`;
          }

          return `${current.name}`;
        }, '');

        const concernedRoles = EffectiveNeedsComponent.getConcernedRoles(groupedFormations, paramedicRules);

        const found = details.find((item: any) => String(item.hospital._id) === String(formation.hospital));

        if (found) {
          found.data.push({
            groupedFormations,
            title,
            concernedRoles,
          });
        }
      }
    }
  }

  static getConcernedRoles(formations: Formation[], paramedicRules: Paramedical[]): Role[] {
    const roles: Role[] = [];
    const addedRoles: any = {};

    for (const formation of formations) {
      const paramedicRule = paramedicRules.find((item: any) => String(item.formation?._id) === String(formation._id));

      if (paramedicRule) {
        for (const role of paramedicRule.roles ?? []) {
          if (!addedRoles[String(role.roleId._id)]) {
            addedRoles[String(role.roleId._id)] = true;
            roles.push(role.roleId);
          }
        }
      }
    }

    return roles;
  }

  static constructEffectiveOfDetailsDay(details: DayDetails[], effective: Effective): void {
    // Init
    for (const item of details) {
      // Init total effective by service
      if (item.morning) {
        item.morning.effective = 0;
      } else {
        item.morning = {
          effective: 0,
        };
      }

      if (item.afternoon) {
        item.afternoon.effective = 0;
      } else {
        item.afternoon = {
          effective: 0,
        };
      }

      // Init effective of grouped formations
      for (const data of item.data) {
        if (data.morning) {
          data.morning.effective = 0;
        } else {
          data.morning = {
            effective: 0,
          };
        }

        if (data.afternoon) {
          data.afternoon.effective = 0;
        } else {
          data.afternoon = {
            effective: 0,
          };
        }
      }
    }

    if (!effective) {
      return;
    }

    if (Array.isArray(effective.morningNurseEffectif)) {
      // Loop through this.effective.morningNurseEffectif
      for (const item of effective.morningNurseEffectif) {
        // Add to total by service
        const hospital = details.find(
          (item2: any) => String(item2.hospital._id) === String(item.formations[0].hospital)
        );
        if (hospital) {
          hospital.morning!.effective! += item.morningNurses;
        }

        // Add to total by group of formation
        const data: Data = EffectiveNeedsComponent.findCorrespondingGroupInDetails(details, item.formations)!;

        if (data) {
          data.morning!.effective! += item.morningNurses;
        }
      }
    }

    if (Array.isArray(effective.afternoonNurseEffectif)) {
      // Loop through this.effective.afternoonNurseEffectif
      for (const item of effective.afternoonNurseEffectif) {
        // Add to total by service
        const hospital = details.find(
          (item2: any) => String(item2.hospital._id) === String(item.formations[0].hospital)
        );
        if (hospital) {
          hospital.afternoon!.effective! += item.afternoonNurses;
        }

        // Add to total by group of formation
        const data: Data = EffectiveNeedsComponent.findCorrespondingGroupInDetails(details, item.formations)!;

        if (data) {
          data.afternoon!.effective! += item.afternoonNurses;
        }
      }
    }
  }

  static findCorrespondingGroupInDetails(details: DayDetails[], formations: Formation[]): Data | undefined {
    if (!formations || formations.length === 0) {
      return undefined;
    }

    const hospital = details.find((item: any) => String(item.hospital._id) === String(formations[0].hospital));

    if (hospital) {
      return hospital.data.find((item: any) => {
        for (const f of formations) {
          const found = item.groupedFormations.find((item2: any) => String(item2._id) === String(f._id));

          if (!found) {
            return false;
          }
        }

        return true;
      });
    }

    return undefined;
  }

  static constructEffectiveOfDetailsNight(details: NightDetails[], effective: NightEffective): void {
    // Init
    for (const item of details) {
      item.effective = 0;
      item.needs = 0;

      for (const item2 of item.data) {
        item2.effective = 0;
        item2.needs = 0;
      }
    }

    if (!effective) {
      return;
    }

    const reasonsByHospital: any = {};
    const effectifByHospital: any = {};

    for (const item of effective.nurses) {
      const hospitalOfFormations = String(item.formations[0].hospital);

      if (!reasonsByHospital[hospitalOfFormations]) {
        reasonsByHospital[hospitalOfFormations] = [];
      }

      if (!effectifByHospital[hospitalOfFormations]) {
        effectifByHospital[hospitalOfFormations] = item.count;
      }

      // Check if we already have the reason
      for (const item2 of item.details) {
        const found = reasonsByHospital[hospitalOfFormations].find(
          (el: any) => String(el._id) === String(item2.reasonMap._id)
        );

        if (!found) {
          reasonsByHospital[hospitalOfFormations].push(item2);
        }
      }
    }

    // Construct nightDetails
    for (const item of details) {
      const hospitalId = String(item.hospital._id);
      const reasonsOfHospital = reasonsByHospital[hospitalId] || [];

      const data: NightData[] = [];

      for (const reasonOfHospital of reasonsOfHospital) {
        const item2: NightData = {
          reasonMap: reasonOfHospital.reasonMap,
          needs: 0,
          effective: reasonOfHospital.count,
        };

        data.push(item2);
      }

      item.data = data;
      item.effective = effectifByHospital[hospitalId];
    }
  }

  static constructNeedsOfDetailsDay(_currentPopulation: any, details: DayDetails[], needs: Need[]): void {
    // Init
    for (const item of details) {
      // Init total needs by service
      if (item.morning) {
        item.morning.needs = 0;
      } else {
        item.morning = {
          needs: 0,
        };
      }

      if (item.afternoon) {
        item.afternoon.needs = 0;
      } else {
        item.afternoon = {
          needs: 0,
        };
      }

      // Init needs of grouped formations
      for (const data of item.data) {
        if (data.morning) {
          data.morning.needs = 0;
        } else {
          data.morning = {
            needs: 0,
          };
        }

        if (data.afternoon) {
          data.afternoon.needs = 0;
        } else {
          data.afternoon = {
            needs: 0,
          };
        }
      }
    }

    if (!needs) {
      return;
    }

    // loop through the rooms to get the associated needs while checking
    // those are the one of the current hospital
    // if(currentPopulation) {
    for (const room of needs) {
      const nursesNeedsPerRoleAndRoom = room.NursesNeedPerRole;
      for (const item of nursesNeedsPerRoleAndRoom) {
        if (item?.role) {
          const hospitalData = details.find((item2: any) => String(item2.hospital._id) === String(item.role.hospital));

          if (hospitalData) {
            // Add needs to total by service
            hospitalData.morning!.needs! += item.morningNursesNeeds;
            hospitalData.afternoon!.needs! += item.afternoonNursesNeeds;

            for (const item3 of hospitalData.data) {
              const isRoleConcerned = item3.concernedRoles?.some(
                (role: any) => String(role._id) === String(item.role._id)
              );
              if (isRoleConcerned) {
                item3.morning!.needs! += item.morningNursesNeeds;
                item3.afternoon!.needs! += item.afternoonNursesNeeds;
              }
            }
          }
        }
      }
    }
  }

  static constructNeedsOfDetailsNight(details: NightDetails[], needs: NightNeed[]): void {
    if (!needs) {
      return;
    }

    // Loop through this.needs
    for (const item of needs) {
      const hospitalData = details.find((item2: any) => String(item2.hospital._id) === String(item.reasonMap.hospital));

      if (hospitalData) {
        // Add needs to total by service
        hospitalData.needs! += item.need;

        // Add missing reasons
        for (const item3 of needs) {
          if (
            !hospitalData.data.find(
              (reasonsmaps: any) => String(reasonsmaps.reasonMap._id) === String(item3.reasonMap._id)
            )
          ) {
            hospitalData.data.push({
              reasonMap: item3.reasonMap,
              effective: 0,
              needs: 0,
            });
          }
        }

        // Add needs to grouped formations
        for (const item3 of hospitalData.data) {
          if (String(item3.reasonMap?._id) === String(item.reasonMap._id)) {
            item3.needs! += item.need;
          }
        }
      }
    }
  }

  static constructRoleNeedsOfDay(roleNeedsTotal: any, details: DayDetails[], needs: Need[]) {
    roleNeedsTotal.morning = 0;
    roleNeedsTotal.afternoon = 0;
    roleNeedsTotal.needsByRole = [];

    for (const room of needs) {
      const nursesNeedsPerRoleAndRoom = room.NursesNeedPerRole;
      for (const item of nursesNeedsPerRoleAndRoom) {
        if (item && item.role) {
          const hospitalData = details.find(
            (item2: any) => String(item2.hospital._id) === String(item.role.hospital)
          )?.hospital;
          roleNeedsTotal.hospital = hospitalData || {};
          if (hospitalData && item.role) {
            // Add needs to total by service
            roleNeedsTotal.morning += item.morningNursesNeeds;
            roleNeedsTotal.afternoon += item.afternoonNursesNeeds;

            const roleIndex = roleNeedsTotal.needsByRole.findIndex(
              (needRole: any) => needRole.role._id === item.role._id
            );
            if (roleIndex >= 0) {
              roleNeedsTotal.needsByRole[roleIndex].morning += item.morningNursesNeeds;
              roleNeedsTotal.needsByRole[roleIndex].afternoon += item.afternoonNursesNeeds;
            } else {
              const needRole = {
                role: item.role,
                afternoon: item.afternoonNursesNeeds,
                morning: item.morningNursesNeeds,
              };
              roleNeedsTotal.needsByRole.push(needRole);
            }
          }
        }
      }
    }

    roleNeedsTotal.needsByRole.sort((a: any, b: any) => {
      const priorityA = a.role?.priority ?? Infinity;
      const priorityB = b.role?.priority ?? Infinity;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      const nameA = a.role?.name?.toLowerCase() ?? '';
      const nameB = b.role?.name?.toLowerCase() ?? '';

      return nameA.localeCompare(nameB);
    });
  }

  ngOnInit() {
    if (this.date) {
      const today = new Date();
      const currentDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      const input = new Date(this.date);
      const inputDateOnly = new Date(input.getFullYear(), input.getMonth(), input.getDate());

      this.isPastDate = inputDateOnly < currentDateOnly;
    }

    this.subscribeToOpenDetailsPopupSubject();
  }

  updateTotalNeedsEffectif() {
    if (this.currentPopulation === 'none') {
      this.totalNeedsEffectif = JSON.parse(JSON.stringify(this.total));
    } else {
      const selectedPopulation: any = this.populationsTotal.find((population: any) => population.selected);
      if (selectedPopulation) {
        this.totalNeedsEffectif = {
          morning: selectedPopulation.morning,
          afternoon: selectedPopulation.afternoon,
        };
      }
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['currentPopulation'] && this.totalNeedsEffectif) {
      if (this.currentPopulation !== 'none') {
        for (const population of this.populationsTotal) {
          population.selected = population._id === this.currentPopulation;
        }
      }
      this.updateTotalNeedsEffectif();
    }

    if (changes['currentPopulation'] && this.period === PERIOD.NIGHT && this.nightDetails?.length && this.effective) {
      this.constructNightTotal();
      this.nightObjectifStatus = EffectiveNeedsComponent.calculateNightObjectifStatus(this.nightDetails);
      if (!this.isNotReadyYet || !this.isMensuel) {
        this.onObjectifStatusChange.emit(this.nightObjectifStatus);
      }
    }

    if (this.isFetching) {
      this.isLoading = true;
    }

    if (this.notInitialized && this.currentPopulation && this.populations.length > 0) {
      this.notInitialized = false;
      this.populationsTotal = this.populations.map((population: any) => {
        return {
          _id: population._id,
          selected: population._id === this.currentPopulation,
          name: population.name,
          formations: population.formations,
          formationsNames: population.formationsNames,
          profilesIds: population.profilesIds,
          roomsPerimeter: population.roomsPerimeter,
          residency: population.residency,
          includeInTotalCount: population.includeInTotalCount,
          morning: {
            effective: 0,
            needs: 0,
          },
          afternoon: {
            effective: 0,
            needs: 0,
          },
        };
      });
    }

    const isDayPeriod = this.period === PERIOD.DAY;
    const isNightPeriod = this.period === PERIOD.NIGHT;

    // Re-initialize details if formations input changes
    const formationsChanged = !!changes['formations'];
    const needsInitialization =
      formationsChanged ||
      (isDayPeriod && this.formations && this.paramedicRules && !this.details) ||
      (isNightPeriod && !this.nightDetails);
    let initializedJustNow = false;

    if (needsInitialization) {
      this.initConcernedHospitals();
      this.constructCoreStructureOfDetails();
      initializedJustNow = true;
    }

    const shouldConstructEffective = changes['effective'] || (this.effective && initializedJustNow);

    if (shouldConstructEffective && !this.isTVO) {
      if (isDayPeriod && this.details) {
        EffectiveNeedsComponent.constructEffectiveOfDetailsDay(this.details, this.effective as Effective);
      }

      if (isNightPeriod && this.nightDetails) {
        EffectiveNeedsComponent.constructEffectiveOfDetailsNight(this.nightDetails, this.effective as NightEffective);
      }
    }

    const shouldConstructNeeds =
      changes['needs'] || changes['nightNeeds'] || formationsChanged || (this.needs && initializedJustNow);

    if (shouldConstructNeeds) {
      if (isDayPeriod && this.details) {
        EffectiveNeedsComponent.constructNeedsOfDetailsDay(this.currentPopulation, this.details, this.needs!);
        if (this.isTVO) {
          EffectiveNeedsComponent.constructRoleNeedsOfDay(this.roleNeedsTotal, this.details, this.needs!);
        }
      }

      if (isNightPeriod && this.nightDetails) {
        EffectiveNeedsComponent.constructNeedsOfDetailsNight(this.nightDetails, this.nightNeeds!);
      }
    }

    if ((this.details || this.nightDetails) && (shouldConstructEffective || shouldConstructNeeds)) {
      if (isDayPeriod && this.details && this.details.length > 0 && this.needs && this.effective) {
        this.constructDayTotal();
        this.dayObjectifStatus = EffectiveNeedsComponent.calculateDayObjectifStatus(this.details);
        if (!this.isNotReadyYet || !this.isMensuel) {
          this.onObjectifStatusChange.emit(this.dayObjectifStatus);
        }
      }

      if (isNightPeriod && this.nightDetails && this.nightDetails.length > 0 && this.effective) {
        this.constructNightTotal();
        this.nightObjectifStatus = EffectiveNeedsComponent.calculateNightObjectifStatus(this.nightDetails);
        if (!this.isNotReadyYet || !this.isMensuel) {
          this.onObjectifStatusChange.emit(this.nightObjectifStatus);
        }
      }
    }
    // Recalculate totalNeedsEffectif if formations input changes
    if (formationsChanged) {
      this.constructDayTotal();
    }
    if (this.date) {
      const today = new Date();
      const currentDateOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());

      const input = new Date(this.date);
      const inputDateOnly = new Date(input.getFullYear(), input.getMonth(), input.getDate());

      this.isPastDate = inputDateOnly < currentDateOnly;
    }

    if (!this.isFetching && ((this.details && this.total) || (this.nightDetails && this.nightTotal != null))) {
      this.isLoading = false;
    }
  }

  initConcernedHospitals(): void {
    if (this.hospitals) {
      this.concernedHospitals = this.hospitals;
    } else {
      this.concernedHospitals = this.userService.getSelectedHospitals();
    }
  }

  constructCoreStructureOfDetails(): void {
    const details: DayDetails[] | NightDetails[] = [];

    for (const h of this.concernedHospitals ?? []) {
      const found = this.currentUser.profile?.hospitals?.find((item: any) => String(item._id) === String(h));
      details.push({
        hospital: found as Hospital,
        data: [],
      });
    }

    if (this.period === PERIOD.DAY) {
      EffectiveNeedsComponent.constructCoreStructureOfDetailsDay(
        details as DayDetails[],
        this.formations ?? [],
        this.paramedicRules ?? []
      );
      this.details = details as DayDetails[];
    } else {
      this.nightDetails = details as NightDetails[];
    }
  }

  objectifStatus(): any {
    if (this.status) {
      return this.status;
    }
    if (!this.details) {
      return undefined;
    }

    // Calculate global totals
    let globalMorningEffectif = 0;
    let globalMorningNeeds = 0;
    let globalAfternoonEffectif = 0;
    let globalAfternoonNeeds = 0;

    for (const item of this.details) {
      globalMorningEffectif += item.morning?.effective ?? 0;
      globalMorningNeeds += item.morning?.needs ?? 0;
      globalAfternoonEffectif += item.afternoon?.effective ?? 0;
      globalAfternoonNeeds += item.afternoon?.needs ?? 0;
    }

    const globalMorningOK = globalMorningEffectif >= globalMorningNeeds;
    const globalAfternoonOK = globalAfternoonEffectif >= globalAfternoonNeeds;

    if (this.currentPopulation && this.currentPopulation !== 'none') {
      const selectedPop = this.populationsTotal.find((p: any) => p._id === this.currentPopulation);
      if (selectedPop) {
        const popMorningOK = (selectedPop.morning?.effective ?? 0) >= (selectedPop.morning?.needs ?? 0);
        const popAfternoonOK = (selectedPop.afternoon?.effective ?? 0) >= (selectedPop.afternoon?.needs ?? 0);

        // Set all hospitals to match selected population status
        for (const item of this.details) {
          item.objectifStatus = !popMorningOK || !popAfternoonOK ? 'ERROR' : 'OK';
        }

        // Return day status based on selected population
        if (!popMorningOK || !popAfternoonOK) {
          return 'ERROR';
        }
        return 'OK';
      }
    }

    const populationsToCheck = this.populationsTotal.filter((pop: any) => pop.includeInTotalCount === true);
    let anyHospitalFails = false;

    // Set each hospital status independently
    for (const item of this.details) {
      let hospitalStatus = 'OK';

      // Check hospital's own total
      const hospitalMorningOK = (item.morning?.effective ?? 0) >= (item.morning?.needs ?? 0);
      const hospitalAfternoonOK = (item.afternoon?.effective ?? 0) >= (item.afternoon?.needs ?? 0);

      if (!hospitalMorningOK || !hospitalAfternoonOK) {
        hospitalStatus = 'ERROR';
      } else {
        // If hospital's own total is OK, check formations or populations
        if (this.populationsTotal.length === 0) {
          // No populations => check formations
          for (const formation of item.data) {
            const formationMorningOK = (formation.morning?.effective ?? 0) >= (formation.morning?.needs ?? 0);
            const formationAfternoonOK = (formation.afternoon?.effective ?? 0) >= (formation.afternoon?.needs ?? 0);

            if (!formationMorningOK || !formationAfternoonOK) {
              hospitalStatus = 'ERROR';
              break;
            }
          }
        } else {
          // Populations exist => check populations with includeInTotalCount=true
          if (populationsToCheck.length > 0) {
            for (const population of populationsToCheck) {
              const popData = this.populationsTotal.find((p: any) => p._id === population._id);

              if (popData) {
                const hospitalFormationIds = item.data
                  .flatMap((d: any) => d.groupedFormations || [])
                  .map((f: any) => f._id);

                const populationBelongsToHospital = popData.formations.some((formId: string) =>
                  hospitalFormationIds.includes(formId)
                );

                if (populationBelongsToHospital) {
                  const popMorningOK = (popData.morning?.effective ?? 0) >= (popData.morning?.needs ?? 0);
                  const popAfternoonOK = (popData.afternoon?.effective ?? 0) >= (popData.afternoon?.needs ?? 0);

                  if (!popMorningOK || !popAfternoonOK) {
                    hospitalStatus = 'ERROR';
                    break;
                  }
                }
              }
            }
          }
        }
      }

      item.objectifStatus = hospitalStatus;

      if (hospitalStatus === 'ERROR') {
        anyHospitalFails = true;
      }
    }

    // Global day status
    // Day fails if: global total fails OR any hospital fails
    if (!globalMorningOK || !globalAfternoonOK || anyHospitalFails) {
      return 'ERROR';
    }

    return 'OK';
  }

  constructDayTotal(): void {
    let total: TotalCombined | TotalSeparated;

    this.totalNeedsEffectif = {
      morning: {
        effective: 0,
        needs: 0,
      },
      afternoon: {
        effective: 0,
        needs: 0,
      },
    };

    const tmpTotal: TotalSeparated = {
      morning: {
        effective: 0,
        needs: 0,
      },
      afternoon: {
        effective: 0,
        needs: 0,
      },
    };

    for (const population of this.populationsTotal) {
      population.morning.effective = 0;
      population.morning.needs = 0;
      population.afternoon.effective = 0;
      population.afternoon.needs = 0;
    }

    // Calculate effective
    if (this.effective) {
      if (Array.isArray((this.effective as Effective).morningNurseEffectif)) {
        for (const item of (this.effective as Effective).morningNurseEffectif) {
          const formationId = item.formations.map((formation: any) => formation._id)[0];
          for (const population of this.populationsTotal) {
            if (population.formations.includes(formationId)) {
              if (
                (population.profilesIds && population.profilesIds.length > 0) ||
                (population.residency && population.residency.length === 1)
              ) {
                if ((this.effective as Effective).morningNurseEffectifWithProfiles) {
                  const correspondanceInDataWithProfiles = (
                    this.effective as Effective
                  )?.morningNurseEffectifWithProfiles?.find((data: any) => data.population === population._id);
                  if (correspondanceInDataWithProfiles) {
                    const relatedHeadCounts = correspondanceInDataWithProfiles.effectif;
                    const relatedHeadCountItem = relatedHeadCounts.find(
                      (headCount: any) => headCount.formations[0]._id === formationId
                    );
                    if (relatedHeadCountItem) {
                      population.morning.effective += relatedHeadCountItem.morningNurses;
                    }
                  }
                }
              } else {
                population.morning.effective += item.morningNurses;
              }
            }
          }
          if (this.isSmartPlanningSideColumnLayout || this.isSmartPlanningDetailsLayout) {
            const formationsIds = this.formations?.map((formation: any) => formation._id) ?? [];
            if (item.formations.some((formation: any) => formationsIds.includes(formation._id))) {
              tmpTotal.morning.effective += item.morningNurses;
            }
          } else {
            tmpTotal.morning.effective += item.morningNurses;
          }
        }
      }

      if (Array.isArray((this.effective as Effective).afternoonNurseEffectif)) {
        for (const item of (this.effective as Effective).afternoonNurseEffectif) {
          const formationId = item.formations.map((formation: any) => formation._id)[0];
          for (const population of this.populationsTotal) {
            if (population.formations.includes(formationId)) {
              if (
                (population.profilesIds && population.profilesIds.length > 0) ||
                (population.residency && population.residency.length === 1)
              ) {
                if ((this.effective as Effective).afternoonNurseEffectifWithProfiles) {
                  const correspondanceInDataWithProfiles = (
                    this.effective as Effective
                  ).afternoonNurseEffectifWithProfiles?.find((effectif: any) => effectif.population === population._id);
                  if (correspondanceInDataWithProfiles) {
                    const relatedHeadCounts = correspondanceInDataWithProfiles.effectif;
                    const relatedHeadCountItem = relatedHeadCounts.find(
                      (headCount: any) => headCount.formations[0]._id === formationId
                    );
                    if (relatedHeadCountItem) {
                      population.afternoon.effective += relatedHeadCountItem.afternoonNurses;
                    }
                  }
                }
              } else {
                population.afternoon.effective += item.afternoonNurses;
              }
            }
          }
          if (this.isSmartPlanningSideColumnLayout || this.isSmartPlanningDetailsLayout) {
            const formationsIds = this.formations?.map((formation: any) => formation._id) ?? [];
            if (item.formations.some((formation: any) => formationsIds.includes(formation._id))) {
              tmpTotal.afternoon.effective += item.afternoonNurses;
            }
          } else {
            tmpTotal.afternoon.effective += item.afternoonNurses;
          }
        }
      }
    }

    // Calculate needs
    if (this.needs) {
      for (const room of this.needs) {
        const nursesNeedsPerRoleAndRoom = room.NursesNeedPerRole;
        for (const item of nursesNeedsPerRoleAndRoom) {
          for (const population of this.populationsTotal) {
            if (
              !population.roomsPerimeter ||
              (population.roomsPerimeter &&
                (population.roomsPerimeter.includes(room.room) || population.roomsPerimeter.length === 0))
            ) {
              const populationFormations = this.formations?.filter((formation: any) =>
                population.formations.includes(formation._id)
              );
              const populationRoles = EffectiveNeedsComponent.getConcernedRoles(
                populationFormations ?? [],
                this.paramedicRules ?? []
              );
              const rolesIds = populationRoles.map((role: any) => role._id);
              if (rolesIds.includes(item.role._id)) {
                population.morning.needs += item.morningNursesNeeds;
                population.afternoon.needs += item.afternoonNursesNeeds;
              }
            }
          }
        }

        if (this.isSmartPlanningSideColumnLayout || this.isSmartPlanningDetailsLayout) {
          for (const item of nursesNeedsPerRoleAndRoom) {
            const formationsRoles = EffectiveNeedsComponent.getConcernedRoles(
              this.formations ?? [],
              this.paramedicRules ?? []
            );
            const rolesIds = formationsRoles.map((role: any) => role._id);
            if (rolesIds.includes(item.role._id)) {
              tmpTotal.morning.needs += item.morningNursesNeeds;
              tmpTotal.afternoon.needs += item.afternoonNursesNeeds;
            }
          }
        } else {
          for (const item of nursesNeedsPerRoleAndRoom) {
            tmpTotal.morning.needs += item.morningNursesNeeds;
            tmpTotal.afternoon.needs += item.afternoonNursesNeeds;
          }
        }
      }
    }

    if (this.separateMorningAfternoon) {
      total = tmpTotal;
      const population = this.populationsTotal.find((pop: any) => pop.selected);
      this.totalNeedsEffectif = {
        morning: population?.morning ?? 0,
        afternoon: population?.afternoon ?? 0,
      };
    } else {
      total = {
        effective: Math.min(tmpTotal.morning.effective, tmpTotal.afternoon.effective),
        needs: Math.max(tmpTotal.morning.needs, tmpTotal.afternoon.needs),
      };
      for (const population of this.populationsTotal) {
        population.effective = Math.min(population.morning.effective, population.afternoon.effective);
        population.needs = Math.max(population.morning.needs, population.afternoon.needs);
        if (population.selected) {
          this.totalNeedsEffectif = {
            effective: population.effective,
            needs: population.needs,
          };
        }
      }
    }

    if (this.isSmartPlanningDetailsLayout && !this.separateMorningAfternoon) {
      total = {
        effective: Math.max(tmpTotal.morning.effective, tmpTotal.afternoon.effective),
        needs: Math.max(tmpTotal.morning.needs, tmpTotal.afternoon.needs),
      };
      for (const population of this.populationsTotal) {
        population.effective = Math.max(population.morning.effective, population.afternoon.effective);
        population.needs = Math.max(population.morning.needs, population.afternoon.needs);
        if (population.selected) {
          this.totalNeedsEffectif = {
            effective: population.effective,
            needs: population.needs,
          };
        }
      }
    }
    this.total = total;
    if (!this.currentPopulation || this.currentPopulation === 'none') {
      this.totalNeedsEffectif = JSON.parse(JSON.stringify(total));
    }
  }

  formatNumber(num: number): number {
    const rounded = Math.floor(num * 100) / 100;

    return rounded;
  }

  constructNightTotal(): void {
    this.nightTotal = { effective: 0, needs: 0 };

    if (!this.nightDetails) {
      return;
    }

    let effective = 0;
    let needs = this.nightDetails[0].needs ?? 0;

    const selectedPop =
      this.currentPopulation && this.currentPopulation !== 'none'
        ? this.populationsTotal.find((p: any) => p._id === this.currentPopulation)
        : null;

    if (selectedPop && this.effective) {
      const nightEffective = this.effective as any;
      const filteredNurses = this.getNightFilteredNurses(nightEffective, this.currentPopulation!, selectedPop);

      effective = filteredNurses.reduce((sum: number, nurse: any) => sum + nurse.count, 0);

      if (filteredNurses.length > 0) {
        const relevantReasonMapIds = new Set<string>();
        for (const nurse of filteredNurses) {
          for (const detail of nurse.details) {
            relevantReasonMapIds.add(String(detail.reasonMap._id));
          }
        }
        needs = (this.nightNeeds ?? [])
          .filter((need: NightNeed) => relevantReasonMapIds.has(String(need.reasonMap._id)))
          .reduce((sum: number, need: NightNeed) => sum + (need.need ?? 0), 0);
      }
    } else if (this.nightDetails[0].effective) {
      this.nightDetails[0].data.forEach((value: any) => (effective += value.effective));
    }

    this.nightTotal = { effective, needs };
  }

  private getNightFilteredNurses(nightEffective: any, populationId: string, selectedPop: any): any[] {
    if (nightEffective?.nursesByPopulation !== undefined) {
      const populationData = nightEffective.nursesByPopulation.find(
        (p: any) => String(p.populationId) === String(populationId)
      );
      if (populationData !== undefined) {
        return populationData.nurses ?? [];
      }
    }

    const formations: any[] = selectedPop?.formations ?? [];
    const populationFormationIds = new Set<string>(formations.map((id: any) => String(id)));
    return ((nightEffective as NightEffective).nurses ?? []).filter((nurse: any) =>
      nurse.formations.some((f: any) => populationFormationIds.has(String(f._id)))
    );
  }

  private buildFilteredNightDetails(): NightDetails[] | undefined {
    if (!this.nightDetails) {
      return undefined;
    }
    if (!this.currentPopulation || this.currentPopulation === 'none') {
      return this.nightDetails;
    }

    const nightEffective = this.effective as any;
    const selectedPop = this.populationsTotal.find((p: any) => p._id === this.currentPopulation);
    const filteredNurses = this.getNightFilteredNurses(nightEffective, this.currentPopulation!, selectedPop);

    const relevantReasonMapIds = new Set<string>();
    for (const nurse of filteredNurses) {
      for (const detail of nurse.details ?? []) {
        relevantReasonMapIds.add(String(detail.reasonMap._id));
      }
    }

    const filteredEffective = filteredNurses.reduce((sum: number, n: any) => sum + n.count, 0);
    const filteredNeeds = (this.nightNeeds ?? [])
      .filter((need: NightNeed) => relevantReasonMapIds.has(String(need.reasonMap._id)))
      .reduce((sum: number, need: NightNeed) => sum + (need.need ?? 0), 0);

    return this.nightDetails.map((item: NightDetails) => {
      const filteredData: NightData[] = item.data.map((d: NightData) => {
        const rmId = d.reasonMap ? String(d.reasonMap._id) : '';
        const isRelevant = !!rmId && relevantReasonMapIds.has(rmId);
        const effectiveForReason = isRelevant
          ? filteredNurses.reduce((sum: number, nurse: any) => {
              const detail = (nurse.details ?? []).find((det: any) => String(det.reasonMap._id) === rmId);
              return sum + (detail?.count ?? 0);
            }, 0)
          : 0;
        return {
          ...d,
          effective: effectiveForReason,
          needs: isRelevant ? (d.needs ?? 0) : 0,
        };
      });

      return {
        ...item,
        effective: filteredEffective,
        needs: filteredNeeds,
        data: filteredData,
      };
    });
  }

  openDetailsPopup(): void {
    posthog.capture('Open_Details_Needs_Popup', { page_source: 'Smart_Planning_Daily' });
    const subTitle = this.isTVO ? this.dayOfTheWeek : this.formatDate(this.date ?? new Date());
    const title = this.period === 'DAY' ? (this.isTVO ? 'Besoins' : 'Effectif/Besoin') : 'Effectif de nuit';
    const showDiff = this.showDifference;
    let lastUpdateDate = null;
    if (this.lastUpdateDate) {
      lastUpdateDate = this.lastUpdateDate.LastUpdateDate
        ? this.formatDateTime(new Date(this.lastUpdateDate.LastUpdateDate))
        : this.lastUpdateDate.LastUpdateDate;
    }

    const onlyNeeds = this.isTVO;

    if (this.period === PERIOD.DAY && this.isEffectiveNeedsByReasonMaps) {
      let data: any = {
        lastUpdateDate: this.lastUpdateDate,
        formations: this.needsTables.map((needsTable: any) => needsTable.formation),
        selectedFormation: { name: 'All', _id: '0' },
        date: this.date || new Date(),
        isPopUp: true,
        title: 'Effectifs / besoins',
        subTitle: this.titleCasePipe.transform(moment.utc(this.date ?? new Date()).format('dddd D MMMM')),
        nbDays: this.days.length > 0 ? this.days.length : 1,
        days: this.days,
        dayIndex: this.dayIndex,
        needsTables: this.needsTables,
        effectifByReasonMap: this.effectifByReasonMap,
        dayOfTheWeek: this.titleCasePipe.transform(moment(this.date ?? new Date()).format('dddd')),
        showDailyFrise: !(this.campaignId && this.scenarioId),
      };

      if (this.initialReasonMap || this.reasonMap) {
        data = { ...data, reasonMap: this.reasonMap, initialReasonMap: this.initialReasonMap };
      }
      this.dialog.open(SimpleDayNeedsComponent, {
        minWidth: '528px',
        width: '30%',
        position: { top: '0px', right: '0px' },
        height: '100%',
        maxHeight: '100%',
        panelClass: 'daily-frise-dialog',
        data,
      });
    } else if (this.period === PERIOD.NIGHT && this.isEffectiveNeedsByReasonMaps) {
      const nightAccessOption = this.userService.getAccessOptionsOfPage('/hrsuite/besoinsgarde');
      this.dialog.open(anesthNeedsGardeComponent, {
        minWidth: '480px',
        panelClass: ['dark-mode', 'anesth-needs-garde-dialog'],
        data: {
          accessOption: nightAccessOption,
          formationsGroups: this.formationsGroups,
          date: moment.utc(this.date ?? new Date()).format('YYYY-MM-DD'),
          dayOfTheWeek: this.titleCasePipe.transform(moment(this.date ?? new Date()).format('dddd')),
          isPopUp: true,
          title: 'Effectifs / besoins',
          subTitle: this.titleCasePipe.transform(moment.utc(this.date ?? new Date()).format('dddd D MMMM')),
          nbDays: 1,
          campaignId: this.campaignId,
          scenarioId: this.scenarioId,
        },
      });
    } else {
      this.dialog.open(EffectiveNeedsDetailsPopupComponent, {
        data: {
          subTitle,
          title,
          lastUpdateDate,
          showDifference: showDiff,
          onlyNeeds,
          onlyMorning: this.onlyMorning,
          onlyAfternoon: this.onlyAfternoon,
          isCycleConfig: this.isCycleConfig,
          period: this.period,
          details: this.period === PERIOD.DAY ? this.details : this.buildFilteredNightDetails(),
          populationsDetails: onlyNeeds ? this.roleNeedsTotal : this.populationsTotal,
        },
      });
    }
  }

  formatDate(date: Date): string {
    const dayOfWeek = moment(date).format('dddd');
    const dayOfMonth = moment(date).format('DD');
    const month = moment(date).format('MMMM');

    return `${dayOfWeek.slice(0, 1).toUpperCase()}${dayOfWeek.slice(1)} ${dayOfMonth} ${month}`;
  }

  formatDateTime(date: Date): string {
    const dayOfWeek = moment(date).format('dddd');
    const dayOfMonth = moment(date).format('DD');
    const month = moment(date).format('MMMM');
    const time = moment(date).format('HH:mm'); // Add time formatting

    return `${dayOfWeek.slice(0, 1).toUpperCase()}${dayOfWeek.slice(1)} ${dayOfMonth} ${month} ${time}`;
  }

  getDifference(arg: { effective: number; needs: number }): number {
    return this.formatNumber((arg?.effective ?? 0) - (arg?.needs ?? 0));
  }

  getFxLayoutAlign(): string {
    if (this.layout === 'column') {
      return 'start center';
    }

    return 'start start';
  }

  subscribeToOpenDetailsPopupSubject(): void {
    if (this.openDetailsPopupSubject) {
      this.subscriptionManager
        .createRequest(this.openDetailsPopupSubject, 'openDetailsPopupSubject', this.instanceId)
        .subscribe({
          next: () => {
            this.openDetailsPopup();
          },

          error: (error: any) => {
            this.errorService.handleError(error);
          },
        });
    }
  }

  onEffectiveNeedsMouseEnter() {
    this.hoverEffectiveNeeds = true;
  }

  onEffectiveNeedsMouseLeave() {
    this.hoverEffectiveNeeds = false;
  }
}
